import { useState } from 'react';
import { HolidayManager } from '../../../components/HolidayManager';
import { HolidayFormModal } from '../../../components/HolidayFormModal';
import { useHolidayMutations, useHolidaysQuery } from '../../../hooks/data';
import type { HolidayCreate, HolidayEntry } from '../../../models/holiday.types';
import { buildRemoveConfirmation } from '../../../components/HolidayManager/holidayManager.definitions';

interface HolidayTabProps {
    canEdit: boolean;
}

interface ModalState {
    isOpen: boolean;
    holiday: HolidayEntry | null;
}

export function HolidayTab({ canEdit }: HolidayTabProps) {
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [modal, setModal] = useState<ModalState>({ isOpen: false, holiday: null });

    const { holidays, customHolidays, isLoading } = useHolidaysQuery(year);
    const { createHoliday, updateHoliday, removeHoliday, duplicateHolidays } =
        useHolidayMutations();

    const closeModal = () => setModal({ isOpen: false, holiday: null });

    const handleSubmit = (data: HolidayCreate, holidayId?: string) => {
        const options = { onSuccess: closeModal };

        if (holidayId) updateHoliday.mutate({ id: holidayId, data }, options);
        else createHoliday.mutate(data, options);
    };

    const handleRemove = (holiday: HolidayEntry) => {
        if (!window.confirm(buildRemoveConfirmation(holiday.name, holiday.date))) return;
        removeHoliday.mutate(holiday.id);
    };

    return (
        <>
            <HolidayManager
                year={year}
                onYearChange={setYear}
                holidays={holidays}
                isLoading={isLoading}
                canEdit={canEdit}
                canDuplicate={canEdit && customHolidays.length > 0}
                onCreate={() => setModal({ isOpen: true, holiday: null })}
                onEdit={(holiday) => setModal({ isOpen: true, holiday })}
                onRemove={handleRemove}
                onDuplicate={() =>
                    duplicateHolidays.mutate({ sourceYear: year, targetYear: year + 1 })
                }
                isDuplicating={duplicateHolidays.isPending}
            />

            <HolidayFormModal
                isOpen={modal.isOpen}
                holiday={modal.holiday}
                onClose={closeModal}
                onSubmit={handleSubmit}
                isSubmitting={createHoliday.isPending || updateHoliday.isPending}
            />
        </>
    );
}

export default HolidayTab;
